import React from 'react';
import { useParams } from 'react-router-dom';
import { copyFor, routes } from 'config';
import { range, zipWith } from 'lodash-es';
import { Text } from '@perch-labs/shrike';
import SVG from 'react-inlinesvg';
import RecsIllustration from '/images/usage-page/recs.svg';
import SyncIcon from '/images/icons/electricity-house.svg';
import SourceIcon from '/images/icons/checkmark-node.svg';
import UseCleanIcon from '/images/icons/leafed-house.svg';
import PowerIcon from '/images/icons/four-circles.svg';
import AffordableIcon from '/images/icons/tree-alone.svg';
import VerifiedIcon from '/images/icons/check-shield.svg';
import { Container, Page } from 'styles';
import { BackLink } from 'components/back-link';
import { MAX_SECONDARY_PAGE_WIDTH } from 'domain/style';
import { MultipartDescription } from 'components/multipart-description';
import { ErrorPage } from 'components/errors';
import { LoadingPage } from 'components/loading';
import { useImpactStats } from 'hooks/use-impact-stats.hook';
import { VideoLinks } from './video-links';

import { ImpactContainer, ApproachContainer } from './recs.style';

const getCopy = copyFor('usage.recs');
const getBackLinkCopy = copyFor('navigation.backLink.dashboardLocations');

export const Recs = () => {
  const { planId } = useParams<{ planId?: string }>();
  const { usage } = routes({ planId });
  const {
    data: impactStats,
    error,
    loading,
  } = useImpactStats({ planId: Number(planId) });

  if (loading) return <LoadingPage />;
  if (error || !impactStats)
    return <ErrorPage title={getCopy('title')} to={usage} />;

  const { kwh, totalRecsPurchased } = impactStats.carbonEquivalency;

  const paragraphs = getCopy('content.paragraphs').map(paragraph => (
    <Text key={paragraph.slice(0, 10)} margin={{ bottom: '16px' }}>
      {paragraph}
    </Text>
  ));
  const [firstParagraph, secondParagraph, thirdParagraph, ...otherParagraphs] =
    paragraphs;
  const middleParagraphs = otherParagraphs.slice(0, 6);
  const endParagraphs = otherParagraphs.slice(6);

  const idsOne = range(3);
  const iconsOne = [SyncIcon, SourceIcon, UseCleanIcon];
  const descriptionsOne = getCopy('content.captions').map(caption => (
    <Text textStyle="heading400">{caption}</Text>
  ));
  const partsOne = zipWith(
    idsOne,
    iconsOne,
    descriptionsOne,
    (id: number, icon: string, description: React.ReactNode) => ({
      description,
      icon,
      id,
    })
  );

  const idsTwo = range(3);
  const iconsTwo = [PowerIcon, AffordableIcon, VerifiedIcon];
  const descriptionsTwo = getCopy('content.approach.captions').map(text => (
    <Container flex direction="column">
      <Text textStyle="heading700" tag="h3" margin={{ right: '16px' }}>
        {text.title}
      </Text>
      <Text textStyle="paragraph400">{text.caption}</Text>
    </Container>
  ));
  const partsTwo = zipWith(
    idsTwo,
    iconsTwo,
    descriptionsTwo,
    (id: number, icon: string, description: React.ReactNode) => ({
      description,
      icon,
      id,
    })
  );

  return (
    <>
      <BackLink to={usage} location={getBackLinkCopy('usage')} />
      <Page maxWidth={MAX_SECONDARY_PAGE_WIDTH}>
        <Text textStyle="heading800" margin={{ bottom: '16px' }} tag="h1">
          {getCopy('title')}
        </Text>
        <Text textStyle="paragraph400" margin={{ bottom: '32px' }}>
          {getCopy('description', { kwh, recs: totalRecsPurchased.toFixed(1) })}
        </Text>
        <ImpactContainer>
          <Container>
            <Text textStyle="heading900">{totalRecsPurchased.toFixed(1)}</Text>
            <Text textStyle="paragraph400">{getCopy('kwhText', { kwh })}</Text>
          </Container>
          <SVG height="128px" width="128px" src={RecsIllustration} />
        </ImpactContainer>
        {firstParagraph}
        {secondParagraph}
        <MultipartDescription
          margin={{ bottom: '24px', top: '24px' }}
          parts={partsOne}
        />
        {thirdParagraph}
        <VideoLinks />
        {middleParagraphs}
        <ApproachContainer>
          <Text textStyle="heading800" opacity="low" tag="h2">
            {getCopy('content.approach.title')}
          </Text>
          <MultipartDescription
            margin={{ bottom: '24px', top: '24px' }}
            parts={partsTwo}
          />
        </ApproachContainer>
        {endParagraphs}
      </Page>
    </>
  );
};
